// packages/web-ui/src/panels/chat-panel/CapabilityChipRow.tsx
//
// W2-S5 AC #3 — 24 px capability chip row that lives directly under
// `<SessionHeader>`. Renders Git / KB / DA chips, a Tier badge, and a
// "skills active" chip. Each chip is clickable and invokes
// `useOpenResource()` with the correct destination/section.

import type { JSX } from 'react';
import { Cpu, Database, GitBranch, Check, X } from 'lucide-react';
import { useOpenResource } from '../../hooks/useOpenResource.js';

const TIER_LABEL: Record<1 | 2 | 3, string> = {
  1: 'basic',
  2: 'standard',
  3: 'rich',
};

/**
 * Row-local input shape. `tier` is a presentation derivation (see
 * `computeCapabilityTier`) computed by the parent — it is NOT persisted in
 * session meta or the server's capabilities schema.
 */
export interface CapabilityChipRowInput {
  hasGit: boolean;
  hasKbConfig: boolean;
  hasDomainAgent: boolean;
  tier: 1 | 2 | 3;
  activeSkillsCount: number;
}

export interface CapabilityChipRowProps {
  capabilities: CapabilityChipRowInput;
  /** Test seam — overrides `useOpenResource()`. */
  openResource?: (destination: 'settings' | 'kb', section: string | null) => void;
}

export function CapabilityChipRow({
  capabilities,
  openResource,
}: CapabilityChipRowProps): JSX.Element {
  const fallback = useOpenResource();
  const open: NonNullable<CapabilityChipRowProps['openResource']> =
    openResource ??
    ((destination, section) => fallback(destination, section));

  return (
    <div
      role="status"
      aria-label="Capability summary"
      data-testid="capability-chip-row"
      className="sticky top-10 z-10 flex h-6 items-center gap-2 border-b border-soft bg-cream-50 px-3 py-1 text-xs text-taupe-700"
    >
      <CapabilityChip
        testId="chip-git"
        icon={<GitBranch size={12} />}
        label="Git"
        present={capabilities.hasGit}
        ariaLabel={`Git: ${capabilities.hasGit ? 'on' : 'off'}; click to configure`}
        onClick={() => {
          // TODO(W3-S6): git-init flow when !hasGit
          open('settings', 'git');
        }}
      />
      <Separator />
      <CapabilityChip
        testId="chip-kb"
        icon={<Database size={12} />}
        label="KB"
        present={capabilities.hasKbConfig}
        ariaLabel={`KB: ${capabilities.hasKbConfig ? 'configured' : 'not configured'}; click to configure`}
        onClick={() => {
          if (!capabilities.hasKbConfig) {
            open('settings', 'kb-init');
          } else {
            open('kb', null);
          }
        }}
      />
      <Separator />
      <CapabilityChip
        testId="chip-da"
        icon={<Cpu size={12} />}
        label="DA"
        present={capabilities.hasDomainAgent}
        ariaLabel={`Domain agent: ${capabilities.hasDomainAgent ? 'on' : 'off'}; click to configure`}
        onClick={() => open('settings', 'domain-agents')}
      />
      <Separator />
      <button
        type="button"
        data-testid="chip-tier"
        aria-label={`Tier ${capabilities.tier} (${TIER_LABEL[capabilities.tier]}); click to configure providers`}
        onClick={() => open('settings', 'providers')}
        className="rounded-full bg-cream-200 px-2 py-0.5 text-xs hover:bg-cream-300"
      >
        Tier {capabilities.tier} ({TIER_LABEL[capabilities.tier]})
      </button>
      <Separator />
      <button
        type="button"
        data-testid="chip-skills"
        aria-label={`${capabilities.activeSkillsCount} skills active; click to configure`}
        onClick={() => open('settings', 'skills')}
        className="rounded-full bg-cream-200 px-2 py-0.5 text-xs hover:bg-cream-300"
      >
        {capabilities.activeSkillsCount} skills active
      </button>
    </div>
  );
}

interface CapabilityChipProps {
  testId: string;
  icon: JSX.Element;
  label: string;
  present: boolean;
  ariaLabel: string;
  onClick: () => void;
}

function CapabilityChip(props: CapabilityChipProps): JSX.Element {
  return (
    <button
      type="button"
      data-testid={props.testId}
      aria-label={props.ariaLabel}
      onClick={props.onClick}
      className="inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-cream-200"
    >
      {props.icon}
      <span>{props.label}</span>
      {props.present ? (
        <Check size={12} className="text-accent-green" data-testid={`${props.testId}-on`} />
      ) : (
        <X size={12} className="text-accent-red" data-testid={`${props.testId}-off`} />
      )}
    </button>
  );
}

function Separator(): JSX.Element {
  return <span aria-hidden="true" className="text-taupe-300">·</span>;
}
